import { apiClient } from './client'

export interface AttendanceRecord {
  _id: string
  sessionId: string
  sessionTitle: string
  sessionDate: string
  sessionDuration?: number
  studentId: string
  // Flat fields from API
  studentName?: string
  studentEmail?: string
  studentNumber?: string
  // Nested student object (alternative format)
  student?: {
    id: string
    firstName: string
    lastName: string
    email: string
    studentId?: string
  }
  teacher?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  lesson?: {
    id: string
    title: string
    description?: string
  }
  attendanceStatus?: 'present' | 'absent' | 'late'
  status?: 'present' | 'absent' | 'late'
  checkInTime?: string
  checkOutTime?: string
  checkInType?: 'rfid' | 'biometric' | 'manual' | 'qr_code'
  checkOutType?: 'rfid' | 'biometric' | 'manual' | 'qr_code'
  attendanceHours?: number
  terminalId?: string
  location?: string
  notes?: string
  recordedBy?: string
  createdAt?: string
  updatedAt?: string
}

export interface AttendanceStats {
  totalAttendanceRecords?: number
  totalAttendance?: number
  presentCount: number
  absentCount: number
  lateCount: number
  totalHours: number
  avgHours: number
  attendanceRate: number
  checkInMethods?: Record<string, number>
  terminalsUsed?: string[]
  terminalCount?: number
}

export interface GetAttendanceParams {
  page?: number
  limit?: number
  search?: string
  sessionId?: string
  studentId?: string
  teacherId?: string
  status?: 'present' | 'absent' | 'late'
  dateFrom?: string
  dateTo?: string
  streamId?: string
  sortBy?: 'checkInTime' | 'studentName' | 'sessionDate' | 'attendanceHours'
  sortOrder?: 'asc' | 'desc'
  institutionId?: string
}

export interface AttendanceResponse {
  success: boolean
  data: {
    attendance: AttendanceRecord[]
    attendanceRecords: AttendanceRecord[]
    pagination: {
      currentPage: number
      totalPages: number
      totalRecords: number
      hasNextPage: boolean
      hasPrevPage: boolean
      limit: number
    }
    stats: AttendanceStats
  }
  message: string
}

export interface UpdateAttendanceRequest {
  status?: 'present' | 'absent' | 'late'
  checkInTime?: string
  checkOutTime?: string
  attendanceHours?: number
  notes?: string
}

export interface BulkUpdateAttendanceRequest {
  attendanceUpdates: {
    studentId: string
    status?: 'present' | 'absent' | 'late'
    checkInTime?: string
    checkOutTime?: string
    attendanceHours?: number
  }[]
}

export interface AddStudentToAttendanceRequest {
  studentId: string
  status?: 'present' | 'absent' | 'late'
  checkInTime?: string
  notes?: string
}

// Get paginated attendance records with filtering
export const getAttendance = async (params: GetAttendanceParams = {}): Promise<AttendanceResponse> => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/attendance?${searchParams.toString()}`)
  return response.data
}

// Get single attendance record
export const getSingleAttendance = async (sessionId: string, studentId: string) => {
  const response = await apiClient.get(`/attendance/${sessionId}/${studentId}`)
  return response.data
}

// Update single attendance record
export const updateAttendance = async (
  sessionId: string, 
  studentId: string, 
  data: UpdateAttendanceRequest
) => {
  const response = await apiClient.put(`/attendance/${sessionId}/${studentId}`, data)
  return response.data
}

// Bulk update attendance for a session
export const bulkUpdateAttendance = async (
  sessionId: string, 
  data: BulkUpdateAttendanceRequest
) => {
  const response = await apiClient.put(`/attendance/${sessionId}/bulk`, data)
  return response.data
}

// Add student to session attendance
export const addStudentToAttendance = async (
  sessionId: string, 
  data: AddStudentToAttendanceRequest
) => {
  const response = await apiClient.post(`/attendance/${sessionId}/add-student`, data)
  return response.data
}

// Remove student from session attendance
export const removeStudentFromAttendance = async (sessionId: string, studentId: string) => {
  const response = await apiClient.delete(`/attendance/${sessionId}/${studentId}`)
  return response.data
}

// Get attendance statistics
export const getAttendanceStats = async (params: {
  dateFrom?: string
  dateTo?: string
  teacherId?: string
  streamId?: string
  studentId?: string
  institutionId?: string
} = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/attendance/stats?${searchParams.toString()}`)
  return response.data
}

// Quick attendance actions
export const markPresent = async (sessionId: string, studentId: string) => {
  return updateAttendance(sessionId, studentId, { 
    status: 'present',
    checkInTime: new Date().toISOString()
  })
}

export const markAbsent = async (sessionId: string, studentId: string) => {
  return updateAttendance(sessionId, studentId, { 
    status: 'absent',
    checkInTime: undefined,
    checkOutTime: undefined,
    attendanceHours: 0
  })
}

export const markLate = async (sessionId: string, studentId: string) => {
  return updateAttendance(sessionId, studentId, { 
    status: 'late',
    checkInTime: new Date().toISOString()
  })
}

// Bulk mark all students in a session
export const bulkMarkAllPresent = async (sessionId: string, studentIds: string[]) => {
  const now = new Date().toISOString()
  const attendanceUpdates = studentIds.map(studentId => ({
    studentId,
    status: 'present' as const,
    checkInTime: now
  }))
  
  return bulkUpdateAttendance(sessionId, { attendanceUpdates })
}

export const bulkMarkAllAbsent = async (sessionId: string, studentIds: string[]) => {
  const attendanceUpdates = studentIds.map(studentId => ({
    studentId,
    status: 'absent' as const,
    checkInTime: undefined,
    checkOutTime: undefined,
    attendanceHours: 0
  }))
  
  return bulkUpdateAttendance(sessionId, { attendanceUpdates })
}

// Get attendance for a specific session
export const getSessionAttendance = async (sessionId: string, params: Omit<GetAttendanceParams, 'sessionId'> = {}) => {
  return getAttendance({ ...params, sessionId })
}

// Get attendance for a specific student
export const getStudentAttendance = async (studentId: string, params: Omit<GetAttendanceParams, 'studentId'> = {}) => {
  return getAttendance({ ...params, studentId })
}

// Get attendance for a specific teacher
export const getTeacherAttendance = async (teacherId: string, params: Omit<GetAttendanceParams, 'teacherId'> = {}) => {
  return getAttendance({ ...params, teacherId })
}

// Get today's attendance
export const getTodayAttendance = async (params: Omit<GetAttendanceParams, 'dateFrom' | 'dateTo'> = {}) => {
  const today = new Date()
  const startOfDay = new Date(today.setHours(0, 0, 0, 0)).toISOString()
  const endOfDay = new Date(today.setHours(23, 59, 59, 999)).toISOString()
  
  return getAttendance({ 
    ...params, 
    dateFrom: startOfDay, 
    dateTo: endOfDay 
  })
}

// Get weekly attendance
export const getWeeklyAttendance = async (params: Omit<GetAttendanceParams, 'dateFrom' | 'dateTo'> = {}) => {
  const now = new Date()
  const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()))
  startOfWeek.setHours(0, 0, 0, 0)
  const endOfWeek = new Date(now.setDate(startOfWeek.getDate() + 6))
  endOfWeek.setHours(23, 59, 59, 999)
  
  return getAttendance({ 
    ...params, 
    dateFrom: startOfWeek.toISOString(), 
    dateTo: endOfWeek.toISOString() 
  })
}

// Get monthly attendance
export const getMonthlyAttendance = async (params: Omit<GetAttendanceParams, 'dateFrom' | 'dateTo'> = {}) => {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
  
  return getAttendance({ 
    ...params, 
    dateFrom: startOfMonth.toISOString(), 
    dateTo: endOfMonth.toISOString() 
  })
}